// hooks/use-highlights.js — HIGHLIGHTS VIA NOTES TABLE
// ═══════════════════════════════════════════════════════════
// Thin wrapper over /api/user/notes filtered by note_type='highlight'.
// Previous four-color system removed — highlights now carry a single
// palette color (terracotta), matching the note type's color in the
// Notes feature. Display surfaces get { highlights, loadHighlights,
// saveHighlight, deleteHighlight } with the same shape as before,
// sourced from the notes table.
// ═══════════════════════════════════════════════════════════

import { useCallback, useState } from 'react';

export const HIGHLIGHT_COLOR = '#c46050'; // terracotta, matches note_type='highlight' in NOTE_TYPE_COLORS

export function useHighlights({ userId, supabase }) {
  const [highlights, setHighlights] = useState([]);

  const authHeaders = useCallback(async (extra = {}) => {
    const headers = { ...extra };
    if (supabase) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
      } catch { /* non-fatal */ }
    }
    return headers;
  }, [supabase]);

  const loadHighlights = useCallback(async (sessionId) => {
    if (!userId) return;
    try {
      const headers = await authHeaders();
      const qs = new URLSearchParams({ type: 'highlight' });
      if (sessionId) qs.set('sessionId', sessionId);
      const res = await fetch(`/api/user/notes?${qs.toString()}`, { headers });
      if (!res.ok) { setHighlights([]); return; }
      const { notes } = await res.json();
      setHighlights((notes || []).map(shapeFromNote));
    } catch { setHighlights([]); }
  }, [userId, authHeaders]);

  async function saveHighlight({ text, sourceId, sessionId }) {
    if (!userId || !text) return null;
    try {
      const headers = await authHeaders({ 'Content-Type': 'application/json' });
      const res = await fetch('/api/user/notes', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          note_type: 'highlight',
          content: text,
          source_text: text,
          session_id: sessionId || null,
          message_id: sourceId || null,
        }),
      });
      if (!res.ok) return null;
      const { note } = await res.json();
      const shaped = shapeFromNote(note);
      setHighlights(prev => [shaped, ...prev]);
      return shaped;
    } catch (e) { console.error('saveHighlight:', e); return null; }
  }

  async function deleteHighlight(highlightId) {
    setHighlights(prev => prev.filter(h => h.id !== highlightId));
    try {
      const headers = await authHeaders();
      await fetch(`/api/user/notes?id=${encodeURIComponent(highlightId)}`, { method: 'DELETE', headers });
    } catch (e) { console.error('deleteHighlight:', e); }
  }

  return {
    highlights,
    setHighlights,
    loadHighlights,
    saveHighlight,
    deleteHighlight,
  };
}

// Map a notes-table row to the shape consumers expect.
// Keeps old field names (text, source_id, color) so existing display
// code doesn't have to change.
function shapeFromNote(note) {
  return {
    id: note.id,
    text: note.source_text || note.content || '',
    color: 'highlight',           // single bucket now, consumers ignore color value
    source_id: note.message_id || null,
    source_type: note.message_id ? 'message' : null,
    session_id: note.session_id || null,
    created_at: note.created_at,
  };
}
